import React from 'react';
import type { ComponentProps } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { borderRadius, display, maxWidth, position, themeGet, zIndex } from 'styled-system';

import { StateType } from '../../../enums/StateType';
import {
  pointerEvents,
  themeLeft,
  themeRight,
  themeTop,
  whiteSpace,
} from '../../../theme/system-utilities';
import type { WithTheme } from '../../../theme/theme';
import { BodyRegularBR1 } from '../../Typography';
import { TRANSITION_CSS } from './constants';
import { ThemeTextColor } from './enums';
import type { InputProps } from './types';
import { getStateTextColor, getStateType, isTel } from './utils';

const TEL_LEFT_SPACING = '90px';
const ICON_RIGHT_SPACING = '34px';

const UnstyledLabel = styled(BodyRegularBR1)<any>`
  ${display};
  ${position};
  ${themeTop};
  ${themeLeft};
  ${themeRight};
  ${maxWidth};
  ${zIndex};
  ${whiteSpace};
  ${pointerEvents};
  ${borderRadius};
  user-select: none;
  ${TRANSITION_CSS};
`;

const Label = (props: ComponentProps<typeof UnstyledLabel>) => (
  <UnstyledLabel
    as="label"
    display="inline-block"
    position="absolute"
    themeTop={0}
    themeLeft={0}
    themeRight={['sp_16', null, null, 'sp_20']}
    maxWidth="fit-content"
    color="transparent"
    ml={['sp_12', null, null, 'sp_16']}
    my="9px"
    px="sp_2"
    zIndex={1}
    overflow="hidden"
    whiteSpace="nowrap"
    pointerEvents="none"
    borderRadius="small"
    {...props}
  />
);

export const labelActiveCSS = (props: WithTheme<InputProps>) => {
  const { hasError, hasWarning, hasSuccess, isLoading, value, readOnly } = props;
  const stateType = getStateType({
    hasError,
    hasWarning,
    hasSuccess,
    isLoading,
  });
  const textColor = getStateTextColor(stateType, value);

  if (readOnly) return null;

  return css`
    width: auto;
    left: 0;
    transform: translate(0, -10px);
    color: ${themeGet(`colors.${textColor}`)(props)};
    background-color: ${themeGet('colors.sys.neutral.background.default')(props)};

    // We can't reuse Typography component here, styling directly
    // @typography-definition
    font-size: ${themeGet('fontSizes.fs_14')(props)}px;
    line-height: ${themeGet('lineHeights.lh_18')(props)};

    margin-top: 0;
    margin-bottom: 0;
    text-overflow: ellipsis;
  `;
};

export const labelFocusCSS = (props: WithTheme<InputProps>) => {
  if (props.readOnly) return null;

  return css`
    color: ${themeGet(`colors.${ThemeTextColor.Focus}`)(props)};
  `;
};

export const applyLabelCSS = (props: WithTheme<InputProps>) => {
  const { type, hasError, hasWarning, hasSuccess, withIcon, isLoading, value } = props;
  const stateType = getStateType({
    hasError,
    hasWarning,
    hasSuccess,
    isLoading,
  });
  const placeholderColorStyle = css`
    color: ${themeGet(`colors.${ThemeTextColor.Placeholder}`)(props)};
  `;

  return css`
    ${!isTel(type) &&
    stateType !== StateType.Default &&
    withIcon &&
    css`
      right: ${ICON_RIGHT_SPACING};
    `}
    ${isTel(type) &&
    css`
      left: ${TEL_LEFT_SPACING};
    `}
    ${!value && placeholderColorStyle}
  `;
};

export const applyActiveLabelCSS = (props: WithTheme<InputProps>) => {
  const { disabled } = props;
  const labelActiveStyle = css`
    ${labelActiveCSS(props)};
  `;

  return css`
    ${!disabled && !!props.value && labelActiveStyle}
  `;
};

export default Label;
